import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { SortableTableHead } from '../../../components/ui/SortableTableHead';
import { Pagination } from '../../../components/ui/Pagination';
import { FileText, Split, Upload, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { convertValueFromInches, formatDate } from '../../../utils/unitUtils';
import type { SortDirection } from '../../../hooks/useTableSort';
import { getMaterialProjectName } from '../../project/components/dashboard/materials/getMaterialProjectName';
import { getMaterialManufacturerLabel } from '../../project/components/dashboard/materials/getMaterialManufacturerLabel';
import {
  collectCollapsibleGroupIds,
  collectLeavesUnderNode,
  DEFAULT_PIPE_GROUP_BY,
  groupPipeMaterials,
  resolvePipeGroupSelection,
  type PipeGroupField,
} from '../utils/groupPipeMaterials';
import { PipeGroupNavPanel } from './PipeGroupNavPanel';

type DocWithUrl = { fileUrl?: string; fileName?: string; materials?: Array<{ fileUrl?: string }> };

const hasDocuments = (docs: DocWithUrl[] | undefined): boolean =>
  Boolean(docs && docs.length > 0);

export type MaterialCategory = 'Pipe' | 'Forge' | 'Gas' | 'Wire Lot' | 'Coating';

interface PipeDashboardTableProps {
  data: Record<string, unknown>[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  displayUnit?: string;
  onSort?: (key: string) => void;
  getSortDirection?: (key: string) => SortDirection;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onDocumentClick?: (row: Record<string, unknown>) => void;
  onUploadClick?: (row: Record<string, unknown>) => void;
  onEditClick?: (row: Record<string, unknown>) => void;
  onDeleteClick?: (row: Record<string, unknown>) => void;
  onSegmentationClick?: (row: Record<string, unknown>) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  deletingMaterialIds?: Set<string>;
  pipeFitterOnly?: boolean;
  category?: MaterialCategory;
  /** When true and groupByFields is non-empty, nest rows by those fields */
  groupByEnabled?: boolean;
  groupByFields?: PipeGroupField[];
}

function unitHeaderLabel(unit: string): string {
  return unit === 'mm' ? 'MM' : 'IN';
}

/** DB stores inches; show OD / wall / length in the selected unit with 2 decimals. */
function formatStoredInches(value: unknown, unit: string): string {
  if (value == null || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return convertValueFromInches(n, unit).toFixed(2);
}

const renderDocumentsCell = (
  docs: DocWithUrl[] | undefined,
  row: Record<string, unknown>,
  onDocumentClick?: (r: Record<string, unknown>) => void
) => {
  if (!hasDocuments(docs)) {
    return <TableCell className="text-center w-[80px]">—</TableCell>;
  }
  return (
    <TableCell className="text-center w-[80px]">
      <div className="flex items-center justify-center">
        <Button
          type="button"
          className="flex h-8 w-auto min-w-8 items-center justify-center gap-1 px-2 text-blue-600 hover:text-blue-800"
          title="View Documents"
          variant="outline"
          onClick={() => onDocumentClick?.(row)}
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="text-[12px] tabular-nums">{docs!.length}</span>
        </Button>
      </div>
    </TableCell>
  );
};

const renderStatusCell = (status: string | undefined) => (
  <TableCell className="text-center">
    <span
      className={cn(
        'px-2 py-1 text-xs rounded-full',
        status === 'Available' && 'bg-green-100 text-green-800',
        status === 'Consumed' && 'bg-gray-100 text-gray-800'
      )}
    >
      {status ?? '-'}
    </span>
  </TableCell>
);

export const PipeDashboardTable: React.FC<PipeDashboardTableProps> = ({
  data,
  totalItems,
  currentPage,
  pageSize,
  loading,
  displayUnit = 'in',
  onSort,
  getSortDirection,
  onPageChange,
  onPageSizeChange,
  onDocumentClick,
  onUploadClick,
  onEditClick,
  onDeleteClick,
  onSegmentationClick,
  canEdit = true,
  canDelete = canEdit,
  deletingMaterialIds,
  pipeFitterOnly = false,
  category = 'Pipe',
  groupByEnabled = false,
  groupByFields = DEFAULT_PIPE_GROUP_BY,
}) => {
  const unit = displayUnit === 'mm' ? 'mm' : 'in';
  const unitLabel = unitHeaderLabel(unit);
  const sort = onSort ?? (() => undefined);
  const sortDir = getSortDirection ?? (() => null);
  const activeGroupFields = useMemo(
    () =>
      category === 'Pipe' && groupByEnabled && groupByFields.length > 0 ? groupByFields : [],
    [category, groupByEnabled, groupByFields]
  );
  const isPipeGrouped = activeGroupFields.length > 0;
  /** Split nav only when we have rows to group; initial load uses full-width table + border. */
  const showGroupedNav = isPipeGrouped && (!loading || data.length > 0);
  const hiddenGroupCols = useMemo(() => new Set(activeGroupFields), [activeGroupFields]);
  const showCol = (field: PipeGroupField) => !hiddenGroupCols.has(field);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const selectedGroupIdRef = useRef<string | null>(null);
  const collapsedGroupIdsRef = useRef<Set<string>>(new Set());
  const groupConfigKey = useMemo(
    () => `${groupByEnabled}:${activeGroupFields.join('\0')}`,
    [groupByEnabled, activeGroupFields]
  );
  const prevGroupConfigKeyRef = useRef<string | null>(null);

  const groupTree = useMemo(() => {
    if (!isPipeGrouped) return [];
    return groupPipeMaterials(data, activeGroupFields);
  }, [isPipeGrouped, data, activeGroupFields]);

  useEffect(() => {
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedGroupId]);

  useEffect(() => {
    collapsedGroupIdsRef.current = collapsedGroupIds;
  }, [collapsedGroupIds]);

  useEffect(() => {
    if (!isPipeGrouped) {
      setSelectedGroupId(null);
      setCollapsedGroupIds(new Set());
      prevGroupConfigKeyRef.current = groupConfigKey;
      return;
    }

    const resetSelection = prevGroupConfigKeyRef.current !== groupConfigKey;
    prevGroupConfigKeyRef.current = groupConfigKey;

    const { selectedGroupId: nextSelected, collapsedGroupIds: nextCollapsed } =
      resolvePipeGroupSelection(groupTree, selectedGroupIdRef.current, collapsedGroupIdsRef.current, {
        resetSelection,
      });
    setSelectedGroupId(nextSelected);
    setCollapsedGroupIds(nextCollapsed);
  }, [groupTree, isPipeGrouped, groupConfigKey]);

  const groupedLeafRows = useMemo(() => {
    if (!isPipeGrouped) return [];
    return collectLeavesUnderNode(groupTree, selectedGroupId);
  }, [isPipeGrouped, groupTree, selectedGroupId]);

  const toggleGroup = (id: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllGroups = () => {
    setCollapsedGroupIds(new Set());
  };

  const collapseAllGroups = () => {
    setCollapsedGroupIds(collectCollapsibleGroupIds(groupTree));
  };

  const formatGroupLabel = (field: PipeGroupField, label: string) => {
    if (field === 'outerDiameter' && label !== '—') {
      return `${formatStoredInches(label, unit)} ${unitLabel}`;
    }
    return label;
  };

  const pipeExtra = (details: Record<string, unknown> | undefined) =>
    (details?.pipeExtraDetails as Record<string, unknown> | undefined) ?? {};

  const SortHead = ({
    sortKey,
    className,
    children,
  }: {
    sortKey: string;
    className?: string;
    children: React.ReactNode;
  }) => (
    <SortableTableHead sortKey={sortKey} onSort={sort} sortDirection={sortDir(sortKey)} className={className}>
      {children}
    </SortableTableHead>
  );

  const renderRow = (row: Record<string, unknown>, idx: number) => {
    const materialIdKey = row.materialId != null ? String(row.materialId) : '';
    const isDeleting = materialIdKey ? deletingMaterialIds?.has(materialIdKey) : false;
    const details = row.details as Record<string, unknown> | undefined;
    const status = row.materialStatus as string | undefined;
    const date = row.materialDate as string | undefined;
    const docs = row.documents as DocWithUrl[] | undefined;

    if (category === 'Pipe') {
      const projectName = getMaterialProjectName(row);
      const extra = pipeExtra(details);
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          {showCol('projectName') && (
            <TableCell className="whitespace-nowrap max-w-[220px] truncate" title={projectName || undefined}>
              {projectName || '-'}
            </TableCell>
          )}
          <TableCell>{details?.pipeNRI != null ? String(details.pipeNRI) : '-'}</TableCell>
          {showCol('pipeNo') && (
            <TableCell>{details?.pipeNo != null ? String(details.pipeNo) : '-'}</TableCell>
          )}
          {showCol('pipeId') && (
            <TableCell>{details?.pipePipeId != null ? String(details.pipePipeId) : '-'}</TableCell>
          )}
          {showCol('heatNumber') && (
            <TableCell>{details?.pipeHeatNumber != null ? String(details.pipeHeatNumber) : '-'}</TableCell>
          )}
          {showCol('outerDiameter') && (
            <TableCell className="text-center">
              {formatStoredInches(details?.pipeOuterDiameter, unit)}
            </TableCell>
          )}
          <TableCell className="text-center">
            {formatStoredInches(details?.pipeWallThickness, unit)}
          </TableCell>
          <TableCell className="text-center">
            {formatStoredInches(details?.pipeLength, unit)}
          </TableCell>
          {showCol('projectStage') && (
            <TableCell className="text-center">
              {details?.pipeProjectStage != null ? String(details.pipeProjectStage) : '-'}
            </TableCell>
          )}
          {showCol('manufacturer') && (
            <TableCell>
              {getMaterialManufacturerLabel(row as Record<string, unknown>) || '-'}
            </TableCell>
          )}
          {showCol('wbsNumber') && (
            <TableCell>
              {extra.wbsNumber != null
                ? String(extra.wbsNumber)
                : extra.wpsNumber != null
                  ? String(extra.wpsNumber)
                  : '-'}
            </TableCell>
          )}
          {showCol('contractor') && (
            <TableCell>{extra.contractor != null ? String(extra.contractor) : '-'}</TableCell>
          )}
          {renderStatusCell(status)}
          <TableCell className="whitespace-nowrap text-center min-w-[110px]">
            {date ? formatDate(date) : '-'}
          </TableCell>
          {pipeFitterOnly && <TableCell className="text-center">—</TableCell>}
          <TableCell className="text-center w-[80px]">
            <div
              className={cn(
                'flex items-center justify-center gap-2',
                !hasDocuments(docs) ? 'justify-center' : ''
              )}
            >
              {hasDocuments(docs) && (
                <Button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center gap-1 p-0 text-blue-600 hover:text-blue-800"
                  title="View Documents"
                  variant="outline"
                  onClick={() => onDocumentClick?.(row)}
                >
                  <FileText className="h-4 w-4" />
                  <span className="text-[12px] tabular-nums">{docs!.length}</span>
                </Button>
              )}
              <Button
                type="button"
                className="flex h-8 w-8 items-center justify-center p-0 text-blue-600 hover:text-blue-800"
                title="Upload Pipe document"
                aria-label="Upload Pipe document"
                variant="outline"
                disabled={!canEdit || isDeleting}
                onClick={() => onUploadClick?.(row)}
              >
                <Upload className="h-4 w-4" />
              </Button>
            </div>
          </TableCell>
          <TableCell className="text-center w-[80px] sticky right-0 z-10 bg-white">
            <div className="flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-8 w-8 p-0 flex items-center justify-center text-blue-600 hover:text-blue-800"
                title="Edit Material"
                aria-label="Edit material"
                disabled={!canEdit || isDeleting}
                onClick={() => onEditClick?.(row)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-8 w-8 p-0 flex items-center justify-center text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Delete Material"
                aria-label="Delete material"
                disabled={!canDelete || isDeleting}
                onClick={() => onDeleteClick?.(row)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              {details?.pipeProjectStage === 'PQT' && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 w-8 p-0 flex items-center justify-center text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="Segmentation"
                  aria-label="Pipe segmentation"
                  disabled={!canEdit || isDeleting}
                  onClick={() => onSegmentationClick?.(row)}
                >
                  <Split className="h-4 w-4" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (category === 'Forge') {
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.forgeNRI != null ? String(details.forgeNRI) : '-'}</TableCell>
          <TableCell>{details?.forgeWorkOrderNumber != null ? String(details.forgeWorkOrderNumber) : '-'}</TableCell>
          <TableCell>{details?.forgeSerialNumber != null ? String(details.forgeSerialNumber) : '-'}</TableCell>
          <TableCell>{details?.forgeHeatNumber != null ? String(details.forgeHeatNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {details?.forgeProjectStage != null ? String(details.forgeProjectStage) : '-'}
          </TableCell>
          {renderStatusCell(status)}
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Gas') {
      const extra = details?.gasExtraDetails as Record<string, unknown> | undefined;
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.gasType != null ? String(details.gasType) : '-'}</TableCell>
          <TableCell>{extra?.gasSize != null ? String(extra.gasSize) : '-'}</TableCell>
          <TableCell>{details?.gasSerialNumber != null ? String(details.gasSerialNumber) : '-'}</TableCell>
          <TableCell>{details?.gasLotNumber != null ? String(details.gasLotNumber) : '-'}</TableCell>
          <TableCell>{extra?.gasManufacturer != null ? String(extra.gasManufacturer) : '-'}</TableCell>
          <TableCell>{details?.gasBatchNumber != null ? String(details.gasBatchNumber) : '-'}</TableCell>
          <TableCell>{extra?.gasComposition != null ? String(extra.gasComposition) : '-'}</TableCell>
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Wire Lot') {
      const extra = details?.wireExtraDetails as Record<string, unknown> | undefined;
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.wireType != null ? String(details.wireType) : '-'}</TableCell>
          <TableCell>{details?.wireLotNumber != null ? String(details.wireLotNumber) : '-'}</TableCell>
          <TableCell>{extra?.wireSizeNumber != null ? String(extra.wireSizeNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {extra?.wireClassification != null ? String(extra.wireClassification) : '-'}
          </TableCell>
          <TableCell>{extra?.wireManufacturer != null ? String(extra.wireManufacturer) : '-'}</TableCell>
          <TableCell>{details?.wireBatchNumber != null ? String(details.wireBatchNumber) : '-'}</TableCell>
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Coating') {
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.coatingLotNumber != null ? String(details.coatingLotNumber) : '-'}</TableCell>
          <TableCell>
            {(details?.coatingExtraDetails as Record<string, unknown> | undefined)?.coatingManufacturer != null
              ? String((details?.coatingExtraDetails as Record<string, unknown>)?.coatingManufacturer)
              : '-'}
          </TableCell>
          <TableCell>{details?.coatingBatchNumber != null ? String(details.coatingBatchNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {Array.isArray(row.assemblyNumbers) ? (row.assemblyNumbers as string[]).join(', ') || '-' : '-'}
          </TableCell>
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    return null;
  };

  const getHeaders = () => {
    if (category === 'Pipe') {
      return (
        <>
          {showCol('projectName') && <SortHead sortKey="projectName">Project Name</SortHead>}
          <SortHead sortKey="details.pipeNRI">NRI</SortHead>
          {showCol('pipeNo') && <SortHead sortKey="details.pipeNo">Pipe No</SortHead>}
          {showCol('pipeId') && <SortHead sortKey="details.pipePipeId">Pipe Id</SortHead>}
          {showCol('heatNumber') && (
            <SortHead sortKey="details.pipeHeatNumber">Heat Number</SortHead>
          )}
          {showCol('outerDiameter') && (
            <SortHead sortKey="details.pipeOuterDiameter" className="text-center">
              Outer diameter [{unitLabel}]
            </SortHead>
          )}
          <SortHead sortKey="details.pipeWallThickness" className="text-center">
            Wall thickness [{unitLabel}]
          </SortHead>
          <SortHead sortKey="details.pipeLength" className="text-center">
            Length [{unitLabel}]
          </SortHead>
          {showCol('projectStage') && (
            <SortHead sortKey="details.pipeProjectStage" className="text-center">
              Project Stage
            </SortHead>
          )}
          {showCol('manufacturer') && (
            <SortHead sortKey="details.manufacturerLabel">Manufacturer</SortHead>
          )}
          {showCol('wbsNumber') && (
            <SortHead sortKey="details.pipeExtraDetails.wbsNumber">WBS Number</SortHead>
          )}
          {showCol('contractor') && (
            <SortHead sortKey="details.pipeExtraDetails.contractor">Contractor</SortHead>
          )}
          <SortHead sortKey="materialStatus" className="text-center">
            Status
          </SortHead>
          <SortHead sortKey="materialDate" className="whitespace-nowrap text-center min-w-[110px]">
            Date
          </SortHead>
          {pipeFitterOnly && <TableHead className="text-center">Pipe Data</TableHead>}
          <TableHead className="text-center">Documents</TableHead>
          <TableHead className="text-center w-[80px] sticky right-0 z-20 bg-white">Actions</TableHead>
        </>
      );
    }
    if (category === 'Forge') {
      return (
        <>
          <SortHead sortKey="details.forgeNRI">NRI</SortHead>
          <SortHead sortKey="details.forgeWorkOrderNumber">Order Number</SortHead>
          <SortHead sortKey="details.forgeSerialNumber">Serial Number</SortHead>
          <SortHead sortKey="details.forgeHeatNumber">Heat Number</SortHead>
          <SortHead sortKey="details.forgeProjectStage" className="text-center">
            Project Stage
          </SortHead>
          <SortHead sortKey="materialStatus" className="text-center">
            Status
          </SortHead>
          <SortHead sortKey="materialDate" className="text-center w-[100px]">
            Date
          </SortHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Gas') {
      return (
        <>
          <SortHead sortKey="details.gasType">Consumable Type</SortHead>
          <SortHead sortKey="details.gasExtraDetails.gasSize">Size</SortHead>
          <SortHead sortKey="details.gasSerialNumber">Serial Number</SortHead>
          <SortHead sortKey="details.gasLotNumber">Lot Number</SortHead>
          <SortHead sortKey="details.gasExtraDetails.gasManufacturer">Manufacturer</SortHead>
          <SortHead sortKey="details.gasBatchNumber">Batch Number</SortHead>
          <SortHead sortKey="details.gasExtraDetails.gasComposition">Composition</SortHead>
          <SortHead sortKey="materialDate" className="text-center w-[100px]">
            Date
          </SortHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Wire Lot') {
      return (
        <>
          <SortHead sortKey="details.wireType">Consumable Type</SortHead>
          <SortHead sortKey="details.wireLotNumber">Lot/Certification Number</SortHead>
          <SortHead sortKey="details.wireExtraDetails.wireSizeNumber">Size</SortHead>
          <SortHead sortKey="details.wireExtraDetails.wireClassification" className="text-center">
            Classification
          </SortHead>
          <SortHead sortKey="details.wireExtraDetails.wireManufacturer">Manufacturer</SortHead>
          <SortHead sortKey="details.wireBatchNumber">Batch Number</SortHead>
          <SortHead sortKey="materialDate" className="text-center w-[100px]">
            Date
          </SortHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Coating') {
      return (
        <>
          <SortHead sortKey="details.coatingLotNumber">Lot Number</SortHead>
          <SortHead sortKey="details.coatingExtraDetails.coatingManufacturer">Manufacturer</SortHead>
          <SortHead sortKey="details.coatingBatchNumber">Batch Number</SortHead>
          <SortHead sortKey="assemblyNumbers" className="text-center">
            Assembly Number
          </SortHead>
          <SortHead sortKey="materialDate" className="text-center w-[100px]">
            Date
          </SortHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    return null;
  };

  const pipeColSpan = (() => {
    let n = 0;
    if (showCol('projectName')) n += 1;
    n += 1; // NRI
    if (showCol('pipeNo')) n += 1;
    if (showCol('pipeId')) n += 1;
    if (showCol('heatNumber')) n += 1;
    if (showCol('outerDiameter')) n += 1;
    n += 2; // wall, length
    if (showCol('projectStage')) n += 1;
    if (showCol('manufacturer')) n += 1;
    if (showCol('wbsNumber')) n += 1;
    if (showCol('contractor')) n += 1;
    n += 2; // status, date
    if (pipeFitterOnly) n += 1;
    n += 2; // docs, actions
    return n;
  })();

  const colSpan =
    category === 'Pipe'
      ? pipeColSpan
      : category === 'Forge'
        ? 8
        : category === 'Gas'
          ? 9
          : category === 'Wire Lot'
            ? 8
            : 6;

  const tableBody =
    loading ? (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          className="min-h-[220px] border-b border-[#E2E5EA] py-16 text-center align-middle"
        >
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e04530]" />
          </div>
        </TableCell>
      </TableRow>
    ) : data.length > 0 && isPipeGrouped ? (
      groupedLeafRows.length > 0 ? (
        groupedLeafRows.map((row, idx) => renderRow(row, idx))
      ) : (
        <TableRow>
          <TableCell colSpan={colSpan} className="text-center text-heritage-grey py-8">
            No materials in this group
          </TableCell>
        </TableRow>
      )
    ) : data.length > 0 ? (
      data.map((row, idx) => renderRow(row, idx))
    ) : (
      <TableRow>
        <TableCell colSpan={colSpan} className="text-center text-heritage-grey py-8">
          No materials found
        </TableCell>
      </TableRow>
    );

  const materialTable = (
    <Table
      containerClassName="relative w-full"
      className={cn(
        'w-max min-w-full border-separate border-spacing-0',
        category === 'Pipe' ? 'min-w-[1900px]' : 'min-w-[1280px]'
      )}
    >
      <TableHeader className="sticky top-0 z-20 border-b border-[#E2E5EA] bg-white">
        <TableRow>{getHeaders()}</TableRow>
      </TableHeader>
      <TableBody className="[&_tr>td]:border-b [&_tr>td]:border-[#E2E5EA]">
        {tableBody}
      </TableBody>
    </Table>
  );

  const tableFrameClass = cn(
    'min-h-[280px] overflow-hidden rounded-lg border border-[#E2E5EA] bg-white',
    showGroupedNav
      ? 'flex h-[calc(100vh-320px)]'
      : 'max-h-[calc(100vh-320px)] overflow-auto'
  );

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className={tableFrameClass}>
        {showGroupedNav ? (
          <>
            <PipeGroupNavPanel
              tree={groupTree}
              collapsedIds={collapsedGroupIds}
              selectedGroupId={selectedGroupId}
              onToggle={toggleGroup}
              onSelect={setSelectedGroupId}
              onExpandAll={expandAllGroups}
              onCollapseAll={collapseAllGroups}
              formatLabel={formatGroupLabel}
            />
            <div className="min-h-0 min-w-0 flex-1 overflow-auto">{materialTable}</div>
          </>
        ) : (
          materialTable
        )}
      </div>
      <Pagination
        className="relative z-auto mt-0 shrink-0 border-0 bg-transparent px-0 py-2 backdrop-blur-none"
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={pageSize}
        onPageChange={onPageChange}
        onItemsPerPageChange={onPageSizeChange}
        pageSizeOptions={[10, 20, 25, 50, 100, 500]}
        showPageNumbers={true}
        maxVisiblePages={5}
      />
    </div>
  );
};
